import { useEffect, useRef } from "react";
import styled from "styled-components";
import { Icon, Tooltip } from "@dialogueconsulting/sked-ui";
import { FIELDS } from "constants/PostFormFields";
import { SmallLoadingSpinner } from "ui/LoadingSpinner/LoadingSpinner";
import { PlatformIcon } from "entities/accounts";
import { toast } from "react-toastify";
import { MediaModal } from "features/media-library";
import { useOpenableState } from "libs/use-openable-state";
import { VideoCoverSelectorProps } from "./types";
import { isSafariLikeUserAgent, resolveToastPlatformName } from "./utils";
import { Alert } from "ui";
import { AlertType } from "ui/alert";
import { VideoFrameSelector } from "./FrameSelector";
import { skedApi } from "api/_skedApi";
import { Button } from "@/components/ui/button";
import { useVideoCoverState } from "./hooks/useVideoCoverState";
import { useVideoCoverOperations } from "./hooks/useVideoCoverOperations";
import { usePlatformPermissions } from "./hooks/usePlatformPermissions";

export const VideoCoverSelector = ({
  videoUrl,
  originalMediaUrl,
  isLoading: isLoadingVideo,
  videoMedia,
  thumbnails,
  updateField,
  videoCoverImageTimestamp,
  videoCoverPreview = "",
  originalCover = "",
  thumbnailUrl,
  selectedPlatform,
  platforms,
  disabled,
  videoLoadingError,
  onCoverPreviewChange,
}: VideoCoverSelectorProps) => {
  // Initialize hooks
  const {
    state: {
      cover,
      isLoadingThumb,
      isEditing,
      originalTimestamp,
      originalCoverType,
    },
    setCover,
    setIsLoadingThumb,
    setIsEditing,
    setOriginalValues,
  } = useVideoCoverState(videoCoverPreview);

  // Initialize original values only once on mount
  const hasInitializedOriginalValues = useRef(false);

  useEffect(() => {
    if (
      !hasInitializedOriginalValues.current &&
      videoCoverImageTimestamp !== undefined
    ) {
      setOriginalValues({
        timestamp: videoCoverImageTimestamp,
        coverType: videoCoverPreview ? "frame_selected" : "platform_default",
      });
      hasInitializedOriginalValues.current = true;
    }
  }, [videoCoverImageTimestamp, videoCoverPreview, setOriginalValues]);

  const { allowUpload, displayPlatforms, platformFullname } =
    usePlatformPermissions({
      selectedPlatform,
      platforms,
      videoLoadingError,
      videoUrl,
    });

  const {
    onCoverChanged,
    onCoverUploaded,
    onRevertToOriginal,
    generateOriginalCover,
  } = useVideoCoverOperations({
    updateField,
    platforms,
    originalMediaUrl,
    videoUrl,
    thumbnailUrl,
    videoCoverImageTimestamp,
    videoCoverPreview,
    setCover,
    setIsLoadingThumb,
    setOriginalValues,
    platformFullname,
  });

  const isLoading = isLoadingVideo || isLoadingThumb;

  useEffect(() => {
    if (isLoadingVideo) {
      return;
    }

    if (!videoUrl) {
      if (originalCover) {
        setCover("");
        updateField(FIELDS.VIDEO_COVER_ORIGINAL, "");
        updateField(FIELDS.VIDEO_COVER_PREVIEW, "");
        updateField(FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP, 0);
        updateField(FIELDS.VIDEO_COVER_TYPE, "platform_default");
      }
      return;
    }

    if (!originalCover) {
      generateOriginalCover();
    }
  }, [
    originalCover,
    videoUrl,
    isLoadingVideo,
    generateOriginalCover,
    setCover,
  ]);

  useEffect(() => {
    onCoverPreviewChange?.(cover || originalCover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cover, originalCover]);

  // All business logic is now handled by the hooks

  return (
    <Wrap>
      {isSafariLikeUserAgent() && (
        <Alert
          type={AlertType.warning}
          icon={true}
          isExpandable={true}
          title="Safari Compatibility Issue"
          defaultExpanded={false}
        >
          Looks like you are using Safari. The cover selection might not work
          properly due to technical limitations. Please use a different browser
          e.g. Google Chrome or Firefox. Contact support if you have any
          questions.
        </Alert>
      )}
      <Head>
        <Title>Cover</Title>
        {!isEditing &&
          selectedPlatform === "LINKED" &&
          displayPlatforms.map((platform) => (
            <StyledPlatformIcon key={platform.TYPE} type={platform.NAME} />
          ))}
        {isEditing && (
          <Tooltip
            text="Drag or click on a thumbnail and click 'done' to finalize your selection."
            side="right"
            textSize="12px"
            contentWidth="450px"
            container={
              document.querySelector(
                ".modal-giselle.modal-dialog"
              ) as HTMLElement
            }
          >
            <div style={{ position: "relative" }}>
              <Icon name="alert-circle" color="#333333" size={16} />
            </div>
          </Tooltip>
        )}
      </Head>

      {isEditing && (
        <VideoFrameSelector
          videoUrl={videoUrl}
          thumbnails={thumbnails}
          media={videoMedia}
          isLoading={isLoading}
          onClose={() => setIsEditing(false)}
          onChange={(cover: string, timestamp: number) =>
            onCoverChanged(
              cover,
              timestamp,
              `${platformFullname} cover updated.`
            )
          }
        />
      )}

      {!isEditing && (
        <Content>
          {isLoading ? (
            <Cover>
              <SmallLoadingSpinner data-testid="loading-spinner" />
            </Cover>
          ) : (
            <Cover data-testid="video-cover">
              {cover || originalCover ? (
                <img src={cover || originalCover} alt="video-cover" />
              ) : (
                <Icon name="image-01" color="#333333" size={16} />
              )}
            </Cover>
          )}
          {!disabled && (
            <Actions>
              {videoMedia && (
                <Button
                  variant="blue-secondary"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  Select cover
                </Button>
              )}
              {allowUpload && (
                <VideoCoverUploadButton
                  onUpload={onCoverUploaded}
                  platformName={resolveToastPlatformName(selectedPlatform)}
                />
              )}
              {cover && cover !== originalCover && (
                <Button
                  variant="blue-tertiary"
                  size="sm"
                  onClick={() => {
                    onRevertToOriginal(
                      originalCover,
                      originalTimestamp ?? videoCoverImageTimestamp,
                      originalCoverType ?? "platform_default"
                    );
                  }}
                >
                  Revert to original
                </Button>
              )}
            </Actions>
          )}
        </Content>
      )}
    </Wrap>
  );
};

function VideoCoverUploadButton({
  onUpload,
  platformName,
}: {
  onUpload: (url: string, thumbnailUrl: string) => void;
  platformName: string;
}) {
  const MediaLibraryModal = useOpenableState();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelect = async ([media]: any[]) => {
    if (media.type !== "image") {
      toast.error("Selected media is not an image.");
      return;
    }

    if (media?.meta?.width < 1080) {
      toast.warn(
        "Selected image has width less than Instagram's recommended size, they may be pixelated."
      );
    }
    if (media?.url.endsWith(".heic") || media?.url.endsWith(".heif")) {
      // just for video previews we don't support HEIC
      const conversionResult = await skedApi.get<{
        url: string;
        assetId: string;
      }>(`/content-inbox/img-url/${media.asset_id}/jpg`);
      media.url = conversionResult.data.url;
    }

    onUpload(media?.url, media?.thumbnailUrl || media?.url);

    toast.success(`The ${platformName} cover has been successfully updated.`);
    MediaLibraryModal.close();
  };

  return (
    <>
      <MediaModal
        show={MediaLibraryModal.isOpened}
        onHide={MediaLibraryModal.close}
        limit={1}
        onSelect={onSelect}
        selectedAccountIds={[]}
      />
      <Button
        variant="blue-tertiary"
        size="sm"
        onClick={MediaLibraryModal.open}
      >
        Upload cover
      </Button>
    </>
  );
}

const Wrap = styled.div`
  margin: 16px 0;
}
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

const Title = styled.div`
  color: var(--Text-T01, #333);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  margin-right: 8px;
`;
const Content = styled.div`
  display: flex;
  align-items: center;
`;
const Cover = styled.div`
  width: 64px;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--Neutral-N200, #c4c4c4);
  background: var(--Neutral-N50, #f2f2f2);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Actions = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const StyledPlatformIcon = styled(PlatformIcon)`
  width: 14px;
  height: 14px;
  margin-left: 5px;
`;
